import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DrunkButton } from '../components/DrunkButton';
import { DrunkCard } from '../components/DrunkCard';
import { Beer, Sparkles, Users, Brain, RefreshCw } from 'lucide-react';

export const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background with overlay */}
        <div 
          className="absolute inset-0 hero-bg opacity-20"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1671116810289-302594443218?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwbGF1Z2hpbmclMjBwYXJ0eSUyMGJhcnxlbnwwfHx8fDE3Njg1MjIxNzJ8MA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        
        <div className="relative z-10 px-4 py-8 md:py-16">
          {/* Logo and Title */}
          <div className="text-center mb-8 bounce-in">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="text-4xl md:text-5xl">✨</span>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-black">
                Moment Tracker
              </h1>
            </div>
            <p className="font-body text-base md:text-lg text-black max-w-md mx-auto leading-relaxed px-2">
              Capture every moment, idea, and memory. From wild parties to sweet celebrations.
            </p>
          </div>

          {/* Mode Preview Cards */}
          <div className="flex overflow-x-auto gap-3 px-4 pb-4 mb-6 -mx-4 snap-x">
            <div className="snap-center flex-shrink-0 w-24 text-center">
              <div className="w-16 h-16 mx-auto mb-2 bg-pink rounded-2xl border-2 border-black shadow-brutal flex items-center justify-center text-2xl">🍺</div>
              <p className="font-heading text-xs font-bold">Drunk AI</p>
            </div>
            <div className="snap-center flex-shrink-0 w-24 text-center">
              <div className="w-16 h-16 mx-auto mb-2 bg-orange-400 rounded-2xl border-2 border-black shadow-brutal flex items-center justify-center text-2xl">🎉</div>
              <p className="font-heading text-xs font-bold">Party</p>
            </div>
            <div className="snap-center flex-shrink-0 w-24 text-center">
              <div className="w-16 h-16 mx-auto mb-2 bg-rose-300 rounded-2xl border-2 border-black shadow-brutal flex items-center justify-center text-2xl">💝</div>
              <p className="font-heading text-xs font-bold">Keepsake</p>
            </div>
            <div className="snap-center flex-shrink-0 w-24 text-center">
              <div className="w-16 h-16 mx-auto mb-2 bg-emerald-400 rounded-2xl border-2 border-black shadow-brutal flex items-center justify-center text-2xl">🚗</div>
              <p className="font-heading text-xs font-bold">Road Trip</p>
            </div>
            <div className="snap-center flex-shrink-0 w-24 text-center">
              <div className="w-16 h-16 mx-auto mb-2 bg-indigo-400 rounded-2xl border-2 border-black shadow-brutal flex items-center justify-center text-2xl">📋</div>
              <p className="font-heading text-xs font-bold">Event</p>
            </div>
          </div>

          {/* CTA Buttons - Stack on mobile */}
          <div className="flex flex-col gap-3 max-w-sm mx-auto mb-8 px-4">
            <DrunkButton
              variant="primary"
              size="large"
              onClick={() => navigate('/session/new')}
              data-testid="start-session-btn"
              className="w-full justify-center text-lg py-4"
            >
              <span className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Start a Session
              </span>
            </DrunkButton>
            
            <DrunkButton
              variant="secondary"
              size="large"
              onClick={() => navigate('/session/join')}
              data-testid="join-session-btn"
              className="w-full justify-center text-lg py-4"
            >
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Join a Session
              </span>
            </DrunkButton>

            <DrunkButton
              variant="ghost"
              size="default"
              onClick={() => navigate('/session/rejoin')}
              data-testid="rejoin-session-btn"
              className="w-full justify-center"
            >
              <span className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Lost your session? Rejoin
              </span>
            </DrunkButton>
          </div>

          {/* Squiggly Divider */}
          <div className="squiggly-divider mx-4" />

          {/* Features - Stack on mobile */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto px-4">
            <DrunkCard variant="cyan" rotate className="slide-up" style={{ animationDelay: '0.1s' }}>
              <div className="text-center p-2">
                <Beer className="w-10 h-10 mx-auto mb-3" />
                <h3 className="font-heading text-xl font-bold mb-1">Capture Everything</h3>
                <p className="font-body text-sm">Ideas, moments, photos, wishes - all in one place</p>
              </div>
            </DrunkCard>

            <DrunkCard variant="yellow" rotate className="slide-up" style={{ animationDelay: '0.2s' }}>
              <div className="text-center p-2">
                <Users className="w-10 h-10 mx-auto mb-3" />
                <h3 className="font-heading text-xl font-bold mb-1">Group Sessions</h3>
                <p className="font-body text-sm">Invite friends with a code & PIN</p>
              </div>
            </DrunkCard>

            <DrunkCard variant="pink" rotate className="slide-up text-white" style={{ animationDelay: '0.3s' }}>
              <div className="text-center p-2">
                <Brain className="w-10 h-10 mx-auto mb-3" />
                <h3 className="font-heading text-xl font-bold mb-1">AI Summaries</h3>
                <p className="font-body text-sm">Get a beautiful recap of your session</p>
              </div>
            </DrunkCard>
          </div>

          {/* Disclaimer */}
          <div className="mt-8 text-center px-4">
            <p className="font-body text-xs text-black/60 max-w-md mx-auto">
              Perfect for parties, celebrations, road trips, and everything in between ✨
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
